<template>
    <section :aria-labelledby="sectionTitleId" class="mt-5 border-t border-foxy-border pt-4">
        <h6 :id="sectionTitleId" class="text-sm font-extrabold text-foxy-text">Дати читання</h6>

        <div class="mt-2 space-y-3">
            <fieldset
                v-for="field in dateFields"
                :key="field.key"
                :ref="`${field.key}Fieldset`"
                tabindex="-1"
                class="min-w-0 rounded-xl border border-foxy-border bg-foxy-surface/45 p-2.5 outline-none"
                :aria-describedby="describedBy(field.key)"
                :aria-invalid="hasErrors(field.key) ? 'true' : 'false'"
            >
                <legend class="px-1 text-sm font-bold text-foxy-text">{{ field.label }}</legend>

                <template v-if="field.key === 'finished' && finishReadOnly">
                    <div class="mt-1 text-sm font-semibold text-foxy-text">Невідомо</div>
                    <p :id="finishHelpId" class="mt-1 text-xs text-foxy-text-secondary">
                        Для поточного або призупиненого читання дата завершення ще не вказується.
                    </p>
                </template>

                <template v-else>
                    <div>
                        <label :for="controlId(field.key, 'precision')" class="mb-0.5 block text-xs font-semibold text-foxy-text-secondary">
                            Точність
                        </label>
                        <select
                            :id="controlId(field.key, 'precision')"
                            :ref="`${field.key}Precision`"
                            class="acc-modal-input min-h-11 w-full"
                            :value="partial(field.key).precision"
                            :disabled="saving"
                            :aria-invalid="errorFor(field.key, 'precision') ? 'true' : 'false'"
                            :aria-describedby="controlDescribedBy(field.key, 'precision')"
                            @change="changePrecision(field.key, $event.target.value)"
                        >
                            <option v-for="option in precisionOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                        </select>
                        <p
                            v-if="errorFor(field.key, 'precision')"
                            :id="errorId(field.key, 'precision')"
                            class="mt-1 text-sm text-foxy-error"
                            role="alert"
                        >
                            {{ errorFor(field.key, 'precision') }}
                        </p>
                    </div>

                    <div
                        v-if="partial(field.key).precision !== 'unknown'"
                        class="mt-2 grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-[8rem_minmax(0,1fr)_7rem]"
                    >
                        <div>
                            <label :for="controlId(field.key, 'year')" class="mb-0.5 block text-xs font-semibold text-foxy-text-secondary">Рік</label>
                            <input
                                :id="controlId(field.key, 'year')"
                                :ref="`${field.key}Year`"
                                type="text"
                                inputmode="numeric"
                                pattern="[0-9]{4}"
                                maxlength="4"
                                placeholder="РРРР"
                                autocomplete="off"
                                class="acc-modal-input min-h-11 w-full"
                                :value="partial(field.key).year ?? ''"
                                :disabled="saving"
                                :aria-invalid="errorFor(field.key, 'year') ? 'true' : 'false'"
                                :aria-describedby="controlDescribedBy(field.key, 'year')"
                                @input="changeYear(field.key, $event)"
                            />
                            <p v-if="errorFor(field.key, 'year')" :id="errorId(field.key, 'year')" class="mt-1 text-sm text-foxy-error" role="alert">
                                {{ errorFor(field.key, 'year') }}
                            </p>
                        </div>

                        <div v-if="['month', 'day'].includes(partial(field.key).precision)">
                            <label :for="controlId(field.key, 'month')" class="mb-0.5 block text-xs font-semibold text-foxy-text-secondary"
                                >Місяць</label
                            >
                            <select
                                :id="controlId(field.key, 'month')"
                                :ref="`${field.key}Month`"
                                class="acc-modal-input min-h-11 w-full"
                                :value="partial(field.key).month ?? ''"
                                :disabled="saving"
                                :aria-invalid="errorFor(field.key, 'month') ? 'true' : 'false'"
                                :aria-describedby="controlDescribedBy(field.key, 'month')"
                                @change="changeMonth(field.key, $event.target.value)"
                            >
                                <option value="">Місяць</option>
                                <option v-for="month in months" :key="month.value" :value="month.value">{{ month.label }}</option>
                            </select>
                            <p
                                v-if="errorFor(field.key, 'month')"
                                :id="errorId(field.key, 'month')"
                                class="mt-1 text-sm text-foxy-error"
                                role="alert"
                            >
                                {{ errorFor(field.key, 'month') }}
                            </p>
                        </div>

                        <div v-if="partial(field.key).precision === 'day'">
                            <label :for="controlId(field.key, 'day')" class="mb-0.5 block text-xs font-semibold text-foxy-text-secondary">День</label>
                            <select
                                :id="controlId(field.key, 'day')"
                                :ref="`${field.key}Day`"
                                class="acc-modal-input min-h-11 w-full"
                                :value="partial(field.key).day ?? ''"
                                :disabled="saving || !hasValidYearMonth(partial(field.key))"
                                :aria-invalid="errorFor(field.key, 'day') ? 'true' : 'false'"
                                :aria-describedby="controlDescribedBy(field.key, 'day')"
                                @change="changeDay(field.key, $event.target.value)"
                            >
                                <option value="">День</option>
                                <option v-for="day in dayOptions(partial(field.key))" :key="day" :value="day">{{ day }}</option>
                            </select>
                            <p v-if="errorFor(field.key, 'day')" :id="errorId(field.key, 'day')" class="mt-1 text-sm text-foxy-error" role="alert">
                                {{ errorFor(field.key, 'day') }}
                            </p>
                        </div>
                    </div>

                    <p v-if="errorFor(field.key, 'group')" :id="errorId(field.key, 'group')" class="mt-2 text-sm text-foxy-error" role="alert">
                        {{ errorFor(field.key, 'group') }}
                    </p>
                </template>
            </fieldset>
        </div>
    </section>
</template>

<script lang="ts">
const emptyPartial = () => ({ precision: 'unknown', year: null, month: null, day: null });

export default {
    name: 'LibraryReadingDateEditor',
    props: {
        readingId: { type: [Number, String], required: true },
        state: { type: String, required: true },
        started: { type: Object, required: true },
        finished: { type: Object, required: true },
        saving: { type: Boolean, default: false },
        errors: {
            type: Object,
            default: () => ({ started: {}, finished: {}, dateRange: '' }),
        },
        dateRangeErrorId: { type: String, default: '' },
    },
    data() {
        return {
            dateFields: [
                { key: 'started', label: 'Початок' },
                { key: 'finished', label: 'Завершення' },
            ],
            precisionOptions: [
                { value: 'unknown', label: 'Невідомо' },
                { value: 'year', label: 'Лише рік' },
                { value: 'month', label: 'Місяць і рік' },
                { value: 'day', label: 'Точна дата' },
            ],
            months: [
                'Січень',
                'Лютий',
                'Березень',
                'Квітень',
                'Травень',
                'Червень',
                'Липень',
                'Серпень',
                'Вересень',
                'Жовтень',
                'Листопад',
                'Грудень',
            ].map((label, index) => ({ value: index + 1, label })),
        };
    },
    computed: {
        scopeId() {
            return `reading-${this.readingId}`;
        },
        sectionTitleId() {
            return `${this.scopeId}-dates-title`;
        },
        finishReadOnly() {
            return ['active', 'paused'].includes(this.state);
        },
        finishHelpId() {
            return `${this.scopeId}-finished-help`;
        },
    },
    methods: {
        partial(key) {
            return key === 'started' ? this.started : this.finished;
        },
        clonePartial(value) {
            return { ...emptyPartial(), ...(value || {}) };
        },
        emitPartial(key, value) {
            this.$emit('update', { key, value: this.clonePartial(value) });
            this.$emit('clear-errors');
        },
        changePrecision(key, precision) {
            const value = this.clonePartial(this.partial(key));
            value.precision = precision;
            if (precision === 'unknown') Object.assign(value, emptyPartial());
            else if (precision === 'year') Object.assign(value, { month: null, day: null });
            else if (precision === 'month') value.day = null;
            this.emitPartial(key, value);
        },
        changeYear(key, event) {
            const digits = String(event?.target?.value || '')
                .replace(/\D/g, '')
                .slice(0, 4);
            if (event?.target) event.target.value = digits;
            const value = this.clonePartial(this.partial(key));
            value.year = digits || null;
            if (!this.hasValidYearMonth(value) || Number(value.day) > this.daysInMonth(value.year, value.month)) value.day = null;
            this.emitPartial(key, value);
        },
        changeMonth(key, month) {
            const value = this.clonePartial(this.partial(key));
            value.month = month === '' ? null : Number(month);
            if (!this.hasValidYearMonth(value) || Number(value.day) > this.daysInMonth(value.year, value.month)) value.day = null;
            this.emitPartial(key, value);
        },
        changeDay(key, day) {
            const value = this.clonePartial(this.partial(key));
            value.day = day === '' ? null : Number(day);
            this.emitPartial(key, value);
        },
        validYear(year) {
            return /^\d{4}$/.test(String(year || '')) && Number(year) >= 1000 && Number(year) <= 9999;
        },
        hasValidYearMonth(value) {
            return this.validYear(value?.year) && Number.isInteger(Number(value?.month)) && Number(value.month) >= 1 && Number(value.month) <= 12;
        },
        daysInMonth(year, month) {
            if (!this.validYear(year) || !Number.isInteger(Number(month)) || Number(month) < 1 || Number(month) > 12) return 0;
            return new Date(Date.UTC(Number(year), Number(month), 0)).getUTCDate();
        },
        dayOptions(value) {
            return Array.from({ length: this.daysInMonth(value?.year, value?.month) }, (_, index) => index + 1);
        },
        controlId(key, component) {
            return `${this.scopeId}-${key}-${component}`;
        },
        errorId(key, component) {
            return `${this.controlId(key, component)}-error`;
        },
        errorFor(key, component) {
            return this.errors?.[key]?.[component] || '';
        },
        hasErrors(key) {
            return (
                ['group', 'precision', 'year', 'month', 'day'].some((component) => Boolean(this.errorFor(key, component))) ||
                Boolean(this.errors.dateRange)
            );
        },
        describedBy(key) {
            return (
                [
                    key === 'finished' && this.finishReadOnly ? this.finishHelpId : '',
                    this.errorFor(key, 'group') ? this.errorId(key, 'group') : '',
                    this.errors.dateRange ? this.dateRangeErrorId : '',
                ]
                    .filter(Boolean)
                    .join(' ') || undefined
            );
        },
        controlDescribedBy(key, component) {
            return (
                [this.errorFor(key, component) ? this.errorId(key, component) : '', this.errors.dateRange ? this.dateRangeErrorId : '']
                    .filter(Boolean)
                    .join(' ') || undefined
            );
        },
        refElement(name) {
            const value = this.$refs[name];
            return Array.isArray(value) ? value[0] : value;
        },
        focusValidation(target) {
            this.$nextTick(() => {
                const [key, component = 'group'] = String(target || '').split('.');
                const refName = component === 'group' ? `${key}Precision` : `${key}${component.charAt(0).toUpperCase()}${component.slice(1)}`;
                (this.refElement(refName) || this.refElement(`${key}Fieldset`))?.focus();
            });
        },
    },
};
</script>
